/**
 * Live presence of a project (utils/presenceClient.js): `{ status, you, people, agents, refused }`, re-rendered on
 * every change. Opens the project's presence socket while mounted (shared with the page's other users of it).
 * `projectId` null: nothing is opened and the snapshot is empty. `name`: the signed-in person's name, for a project
 * that does not know it yet (a member added by email); any one user of the socket passing it is enough.
 */
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { acquirePresence, getPresenceSnapshot, setPresenceName, setPresenceView, subscribePresence } from '../utils/presenceClient';

export default function useProjectPresence(projectId, { name = '' } = {}) {
  useEffect(() => {
    if (!projectId) { return undefined; }
    return acquirePresence(projectId);
  }, [projectId]);
  useEffect(() => {
    if (projectId && name) { setPresenceName(projectId, name); }
  }, [projectId, name]);

  const subscribe = useCallback((listener) => (projectId ? subscribePresence(projectId, listener) : () => {}), [projectId]);
  const snapshot = useCallback(() => getPresenceSnapshot(projectId), [projectId]);
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/** Publishes where this tab is in the project for one `source` (see setPresenceView) while mounted. */
export function usePresenceView(projectId, source, view) {
  const key = view ? JSON.stringify(view) : '';
  useEffect(() => {
    if (!projectId || !key) { return undefined; }
    setPresenceView(projectId, source, JSON.parse(key));
    return () => setPresenceView(projectId, source, null);
  }, [projectId, source, key]);
}
